import { z } from "zod";

export const SmartCreationTargetingStrategyKindSchema = z.enum([
  "automatic",
  "research",
  "performance",
  "branded",
  "competitor",
]);
export type SmartCreationTargetingStrategyKind = z.infer<
  typeof SmartCreationTargetingStrategyKindSchema
>;

export const SmartCreationStrategyLaneSchema = z.enum([
  "automatic",
  "competitor_keywords",
  "competitor_product_exact",
  "competitor_brand",
  "research_broad",
  "research_phrase",
  "research_category",
  "research_product_expanded",
  "performance_exact",
  "performance_product_exact",
  "branded_keywords",
  "branded_own_asins",
  "branded_own_brand",
]);
export type SmartCreationStrategyLane = z.infer<typeof SmartCreationStrategyLaneSchema>;

export function smartCreationStrategyLaneKind(
  lane: SmartCreationStrategyLane,
): SmartCreationTargetingStrategyKind {
  if (lane === "automatic") return "automatic";
  return lane.split("_")[0] as Exclude<SmartCreationTargetingStrategyKind, "automatic">;
}

export const SmartCreationAutoTargetExpressionSchema = z.enum([
  "close_match",
  "loose_match",
  "substitutes",
  "complements",
]);
export type SmartCreationAutoTargetExpression = z.infer<
  typeof SmartCreationAutoTargetExpressionSchema
>;

export const SmartCreationStrategyEntityIdSchema = z.string().trim().min(1).max(128);
export const SmartCreationStrategyBidSchema = z.number().finite().positive().max(1_000_000);

export const SmartCreationStrategyTargetSourceSchema = z.enum([
  "system_required",
  "current_shop_search_term",
  "current_shop_target_performance",
  "amazon_recommendation",
  "user_entered_seed",
  "product_catalog",
  "current_shop_shared_history",
]);
export type SmartCreationStrategyTargetSource = z.infer<
  typeof SmartCreationStrategyTargetSourceSchema
>;

export const SmartCreationStrategyTargetIntentSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("auto"),
      expressionType: SmartCreationAutoTargetExpressionSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("keyword"),
      keyword: z.string().trim().min(1).max(80),
      matchType: z.enum(["exact", "phrase", "broad"]),
    })
    .strict(),
  z
    .object({
      type: z.literal("product"),
      asin: z
        .string()
        .trim()
        .regex(/^[A-Z0-9]{10}$/),
      matchType: z.enum(["exact", "expanded"]).optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("category"),
      categoryId: z.string().trim().min(1).max(128),
    })
    .strict(),
  z
    .object({
      type: z.literal("category_brand"),
      categoryId: z.string().trim().regex(/^\d+$/),
      brandId: z.string().trim().min(1).max(128),
    })
    .strict(),
]);
export type SmartCreationStrategyTargetIntent = z.infer<
  typeof SmartCreationStrategyTargetIntentSchema
>;

export function canonicalSmartCreationTargetIntentKey(
  intent: SmartCreationStrategyTargetIntent,
): string {
  if (intent.type === "auto") return `auto:${intent.expressionType}`;
  if (intent.type === "keyword") {
    const keyword = intent.keyword.trim().replace(/\s+/g, " ").toLocaleLowerCase("en-US");
    return `keyword:${intent.matchType}:${keyword}`;
  }
  if (intent.type === "product")
    return `product:${intent.asin}${intent.matchType === "expanded" ? ":expanded" : ""}`;
  if (intent.type === "category_brand")
    return `category_brand:${intent.categoryId}:${intent.brandId}`;
  return `category:${intent.categoryId}`;
}

export const SmartCreationStrategyTargetValueSchema = z
  .object({
    id: SmartCreationStrategyEntityIdSchema,
    intent: SmartCreationStrategyTargetIntentSchema,
    bid: SmartCreationStrategyBidSchema,
    included: z.boolean(),
    productAsins: z
      .array(z.string().regex(/^[A-Z0-9]{10}$/))
      .min(1)
      .optional(),
  })
  .strict();

export const SmartCreationStrategyTargetProvenanceSchema = z.discriminatedUnion("mode", [
  z
    .object({
      mode: z.literal("entered"),
      origin: z.enum(["user", "system_required", "legacy"]),
      recordedAt: z.string().datetime().nullable(),
    })
    .strict(),
  z
    .object({
      mode: z.literal("accepted"),
      acceptedAt: z.string().datetime(),
      recommendationId: z.string().uuid(),
      recommendationTargetId: SmartCreationStrategyEntityIdSchema,
      originalRecommendation: z
        .object({
          intent: SmartCreationStrategyTargetIntentSchema,
          bid: SmartCreationStrategyBidSchema,
          included: z.boolean(),
          source: SmartCreationStrategyTargetSourceSchema,
          rationale: z.string().trim().min(1).max(1_000),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      mode: z.literal("overridden"),
      acceptedAt: z.string().datetime(),
      overriddenAt: z.string().datetime(),
      recommendationId: z.string().uuid(),
      recommendationTargetId: SmartCreationStrategyEntityIdSchema,
      originalRecommendation: z
        .object({
          intent: SmartCreationStrategyTargetIntentSchema,
          bid: SmartCreationStrategyBidSchema,
          included: z.boolean(),
          source: SmartCreationStrategyTargetSourceSchema,
          rationale: z.string().trim().min(1).max(1_000),
        })
        .strict(),
    })
    .strict(),
]);
export type SmartCreationStrategyTargetProvenance = z.infer<
  typeof SmartCreationStrategyTargetProvenanceSchema
>;

export const SmartCreationStrategyTargetSchema = SmartCreationStrategyTargetValueSchema.extend({
  provenance: SmartCreationStrategyTargetProvenanceSchema,
})
  .strict()
  .superRefine((target, ctx) => {
    if (target.provenance.mode === "entered") return;
    const original = target.provenance.originalRecommendation;
    const matchesOriginal =
      target.bid === original.bid &&
      target.included === original.included &&
      JSON.stringify(target.intent) === JSON.stringify(original.intent);
    if (target.provenance.mode === "accepted" && !matchesOriginal) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Accepted Target must match its original recommendation",
        path: ["provenance", "mode"],
      });
    }
    if (target.provenance.mode === "overridden" && matchesOriginal) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Overridden Target must differ from its original recommendation",
        path: ["provenance", "mode"],
      });
    }
  });
export type SmartCreationStrategyTarget = z.infer<typeof SmartCreationStrategyTargetSchema>;
